import { c as _c } from "react/compiler-runtime";
import { candleBucketSizeToSeconds, graphql, readFragment, useQuery } from "@opensea/graphql";
import { bn } from "@opensea/utils";
import { useEffect, useState } from "react";
import { MAX_BUCKET_SIZE_FOR_TIMEFRAME } from "@/components/currencies/charts/CurrencyCandlestickChart/constants";
import { useIsCurrencyChartAnnotationEnabled } from "@/hooks/use-flags";
import { useSubscription } from "@/hooks/use-subscription";
import { TokenSwapActivityAnnotation } from "./TokenSwapActivityAnnotation";
import { TokenSwapActivityAnnotationFragment } from "./TokenSwapActivityAnnotation.fragment";
const useTokenSwapActivityAnnotationsFragment = graphql(`
  fragment useTokenSwapActivityAnnotations on TokenSwapActivity {
    id
    timestamp
    fromToken {
      usd
      token {
        unit
        address
        chain {
          identifier
        }
      }
    }
    toToken {
      usd
      token {
        unit
        address
        chain {
          identifier
        }
      }
    }
    ...TokenSwapActivityAnnotation
  }
`, [TokenSwapActivityAnnotationFragment]);
const query = graphql(`
  query TokenSwapActivityAnnotationsQuery($chain: ChainIdentifier!, $token: Address!, $filter: TokenSwapActivityFilterInput!) {
    tokenSwapActivity(chain: $chain, token: $token, filter: $filter) {
      items {
        id
        ...useTokenSwapActivityAnnotations
      }
      nextPageCursor
    }
  }
`, [useTokenSwapActivityAnnotationsFragment]);
const subscription = graphql(`
  subscription TokenSwapActivityAnnotationsSubscription($chain: ChainIdentifier!, $token: Address!, $filter: TokenSwapActivityFilterInput!) {
    tokenSwapActivity(chain: $chain, token: $token, filter: $filter) {
      id
      ...useTokenSwapActivityAnnotations
    }
  }
`, [useTokenSwapActivityAnnotationsFragment]);
const context = {
  suspense: false
};
export function useTokenSwapActivityAnnotations(t0) {
  const $ = _c(28);
  const {
    contract,
    filter,
    pause,
    timeframe
  } = t0;
  const isCurrencyChartAnnotationEnabled = useIsCurrencyChartAnnotationEnabled();
  let t1;
  if ($[0] !== contract.address || $[1] !== contract.chain || $[2] !== filter) {
    t1 = {
      chain: contract.chain,
      token: contract.address,
      filter
    };
    $[0] = contract.address;
    $[1] = contract.chain;
    $[2] = filter;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = !isCurrencyChartAnnotationEnabled || pause;
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      query,
      variables: t1,
      context,
      pause: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      throwOnError: false
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const [t5] = useQuery(t3, t4);
  const {
    data
  } = t5;
  let t6;
  if ($[8] !== data?.tokenSwapActivity.items) {
    t6 = data?.tokenSwapActivity.items ?? [];
    $[8] = data?.tokenSwapActivity.items;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const [activity, setActivity] = useState(t6);
  let t7;
  let t8;
  if ($[10] !== data) {
    t7 = function resetActivityWhenDataChanges() {
      if (data?.tokenSwapActivity.items) {
        setActivity(data.tokenSwapActivity.items);
      }
    };
    t8 = [data];
    $[10] = data;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t9;
  if ($[13] !== contract.address || $[14] !== contract.chain || $[15] !== filter) {
    t9 = {
      chain: contract.chain,
      token: contract.address,
      filter
    };
    $[13] = contract.address;
    $[14] = contract.chain;
    $[15] = filter;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = !isCurrencyChartAnnotationEnabled || pause;
  let t11;
  if ($[17] !== t10 || $[18] !== t9) {
    t11 = {
      query: subscription,
      variables: t9,
      pause: t10
    };
    $[17] = t10;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== activity) {
    t12 = data_0 => {
      setActivity(prev => {
        if (activity.some(item => item.id === data_0.tokenSwapActivity.id)) {
          return prev;
        }
        return [...prev, data_0.tokenSwapActivity];
      });
    };
    $[20] = activity;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  useSubscription(t11, t12);
  let t13;
  if ($[22] !== activity || $[23] !== contract || $[24] !== timeframe) {
    const annotations = activity.flatMap(item_0 => {
      const annotation = readFragment(useTokenSwapActivityAnnotationsFragment, item_0);
      const relevantToken = [annotation.fromToken, annotation.toToken].find(token => token.token.address === contract.address && token.token.chain.identifier === contract.chain);
      if (!relevantToken) {
        return [];
      }
      const isBuy = annotation.toToken === relevantToken;
      const value = bn(relevantToken.usd).dividedBy(relevantToken.token.unit).multipliedBy(isBuy ? 1 : -1).toNumber();
      const date = new Date(annotation.timestamp);
      return [{
        date,
        value,
        annotation
      }];
    }) ?? [];
    const bucketSize = MAX_BUCKET_SIZE_FOR_TIMEFRAME[timeframe ?? "ONE_DAY"];
    const bucketMs = candleBucketSizeToSeconds(bucketSize) * 1000 * 4;
    const grouped = new Map();
    for (const ann of annotations) {
      const key = Math.floor(ann.date.getTime() / bucketMs) * bucketMs;
      const existing = grouped.get(key);
      if (existing) {
        existing.items.push(ann);
        existing.sumMs = existing.sumMs + ann.date.getTime();
        existing.sumValue = existing.sumValue + ann.value;
      } else {
        grouped.set(key, {
          items: [ann],
          sumMs: ann.date.getTime(),
          sumValue: ann.value
        });
      }
    }
    t13 = Array.from(grouped.values()).map(group => {
      const count = group.items.length;
      const avgMs = group.sumMs / count;
      const avgValue = group.sumValue / count;
      return {
        date: new Date(avgMs),
        value: avgValue,
        content: <TokenSwapActivityAnnotation activities={group.items.map(_temp)} contract={contract} />
      };
    });
    $[22] = activity;
    $[23] = contract;
    $[24] = timeframe;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const groupedAnnotations = t13;
  let t14;
  if ($[26] !== groupedAnnotations) {
    t14 = {
      annotations: groupedAnnotations
    };
    $[26] = groupedAnnotations;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  return t14;
}
function _temp(i) {
  return i.annotation;
}